import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import {
  Users,
  UserPlus,
  UserCog,
  Search,
  MoreHorizontal,
  Check,
  X,
  Mail,
  Phone,
  Building2,
  Shield,
  ShieldCheck,
  Key,
  User,
  Trash2,
  Edit,
  RefreshCw,
  Filter,
  Download,
  Upload,
  Crown,
  BadgeCheck,
  Clock,
  MapPin,
  ChevronDown,
  Copy,
  Eye,
  EyeOff,
  Sparkles,
  UserCheck,
  UserX
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Separator } from '../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getErrorMessage } from '../lib/utils';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AssociationMember {
  id: string;
  user_id: string;
  company_id: string;
  association_id: string;
  member_code?: string;
  membership_type: string;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  notes?: string;
  company?: {
    name: string;
    email?: string;
    phone?: string;
    city?: string;
    province?: string;
  };
}

interface AssociationAdmin {
  id: string;
  user_id: string;
  association_id: string;
  can_manage_members: boolean;
  can_manage_sellers: boolean;
  can_manage_groupage: boolean;
  can_manage_settings: boolean;
  can_view_analytics: boolean;
  can_manage_admins: boolean;
  is_primary: boolean;
  created_at: string;
  user?: {
    name: string;
    email: string;
  };
}

interface Association {
  id: string;
  name: string;
  slug: string;
}

// Componente Stat Card compatto
const StatCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  subValue?: string;
  trend?: 'up' | 'down' | 'neutral';
  color?: string;
}> = ({ icon, label, value, subValue, color = 'primary' }) => (
  <div className="flex items-center gap-4 p-4 rounded-xl bg-card border">
    <div className={cn(
      "p-3 rounded-xl",
      color === 'primary' && "bg-primary/10 text-primary",
      color === 'success' && "bg-green-500/10 text-green-600",
      color === 'warning' && "bg-yellow-500/10 text-yellow-600",
      color === 'info' && "bg-blue-500/10 text-blue-600"
    )}>
      {icon}
    </div>
    <div>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {subValue && <p className="text-xs text-muted-foreground">{subValue}</p>}
    </div>
  </div>
);

// Componente Avatar con iniziali
const MemberAvatar: React.FC<{ name: string; size?: 'sm' | 'md' | 'lg' }> = ({ name, size = 'md' }) => {
  const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const sizeClasses = {
    sm: 'h-8 w-8 text-xs',
    md: 'h-10 w-10 text-sm',
    lg: 'h-12 w-12 text-base'
  };
  
  return (
    <Avatar className={sizeClasses[size]}>
      <AvatarFallback className="bg-primary/10 text-primary font-medium">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};

// Componente Filtro Pill
const FilterPill: React.FC<{
  label: string;
  active: boolean;
  onClick: () => void;
  count?: number;
}> = ({ label, active, onClick, count }) => (
  <button
    onClick={onClick}
    className={cn(
      "px-4 py-2 rounded-full text-sm font-medium transition-all",
      active 
        ? "bg-primary text-primary-foreground shadow-md" 
        : "bg-muted text-muted-foreground hover:bg-muted/80"
    )}
  >
    {label}
    {count !== undefined && (
      <span className={cn(
        "ml-2 px-2 py-0.5 rounded-full text-xs",
        active ? "bg-white/20" : "bg-background"
      )}>
        {count}
      </span>
    )}
  </button>
);

const MembersManagementPage: React.FC = () => {
  const { user } = useAuth();
  const [association, setAssociation] = useState<Association | null>(null);
  const [members, setMembers] = useState<AssociationMember[]>([]);
  const [admins, setAdmins] = useState<AssociationAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'members' | 'admins'>('members');
  
  // Filtri
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [verifiedFilter, setVerifiedFilter] = useState<'all' | 'verified' | 'unverified'>('all');
  
  // Dialog stati
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showAddAdminDialog, setShowAddAdminDialog] = useState(false);
  const [showMemberDetailDialog, setShowMemberDetailDialog] = useState(false);
  const [showEditMemberDialog, setShowEditMemberDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedMember, setSelectedMember] = useState<AssociationMember | null>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<AssociationAdmin | null>(null);
  const [deletingMember, setDeletingMember] = useState(false);
  
  // Form stati - Edit Membro
  const [editMemberForm, setEditMemberForm] = useState({
    name: '',
    email: '',
    phone: '',
    member_code: '',
    membership_type: 'standard',
    is_active: true,
    is_verified: false,
    notes: '',
    new_password: ''
  });
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [savingMember, setSavingMember] = useState(false);
  
  // Form stati - Nuovo Membro
  const [memberForm, setMemberForm] = useState({
    email: '',
    name: '',
    company_name: '',
    member_code: '',
    membership_type: 'standard',
    notes: ''
  });
  
  // Form stati - Nuovo Admin
  const [adminForm, setAdminForm] = useState({
    name: '',
    email: '',
    password: '',
    company_name: '',
    permissions: {
      can_manage_members: true,
      can_manage_sellers: true,
      can_manage_groupage: true,
      can_manage_settings: false,
      can_view_analytics: true,
      can_manage_admins: false
    }
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (user?.association_id) {
      fetchData();
    }
  }, [user?.association_id]);

  const fetchData = async () => {
    if (!user?.association_id) return;
    
    try {
      setLoading(true);
      
      const [assocRes, membersRes, adminsRes] = await Promise.all([
        axios.get(`${API}/associations/${user.association_id}`),
        axios.get(`${API}/associations/${user.association_id}/members`),
        axios.get(`${API}/associations/${user.association_id}/admins`)
      ]);
      
      setAssociation(assocRes.data);
      setMembers(membersRes.data.members || []);
      setAdmins(adminsRes.data || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // Genera password sicura
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setAdminForm({ ...adminForm, password });
  };

  // Genera codice socio
  const generateMemberCode = () => {
    const prefix = association?.slug?.toUpperCase().slice(0, 4) || 'MEMB';
    const number = String(members.length + 1).padStart(4, '0');
    setMemberForm({ ...memberForm, member_code: `${prefix}-${number}` });
  };

  // Copia negli appunti
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiato negli appunti`);
  };

  // Apri dialog modifica membro
  const openEditMemberDialog = (member: AssociationMember) => {
    setSelectedMember(member);
    setEditMemberForm({
      name: member.company?.name || '',
      email: member.company?.email || '',
      phone: member.company?.phone || '',
      member_code: member.member_code || '',
      membership_type: member.membership_type,
      is_active: member.is_active,
      is_verified: member.is_verified,
      notes: member.notes || '',
      new_password: ''
    });
    setShowNewPassword(false);
    setShowEditMemberDialog(true);
  };

  // Salva modifiche membro
  const handleSaveMember = async () => {
    if (!selectedMember) return;
    
    if (!editMemberForm.email) {
      toast.error('L\'email è obbligatoria');
      return;
    }
    
    setSavingMember(true);
    
    try {
      // Aggiorna membro (membership)
      await axios.put(`${API}/associations/${user?.association_id}/members/${selectedMember.id}`, {
        member_code: editMemberForm.member_code,
        membership_type: editMemberForm.membership_type,
        is_active: editMemberForm.is_active,
        is_verified: editMemberForm.is_verified,
        notes: editMemberForm.notes,
        // Dati utente/company
        name: editMemberForm.name,
        email: editMemberForm.email,
        phone: editMemberForm.phone
      });
      
      // Se c'è una nuova password, aggiornala
      if (editMemberForm.new_password && editMemberForm.new_password.length >= 8) {
        await axios.post(`${API}/associations/${user?.association_id}/members/${selectedMember.id}/reset-password`, {
          new_password: editMemberForm.new_password
        });
        toast.success('Password aggiornata');
      }
      
      toast.success('Membro aggiornato con successo');
      setShowEditMemberDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingMember(false);
    }
  };

  // Conferma eliminazione membro
  const confirmDeleteMember = (member: AssociationMember) => {
    setSelectedMember(member);
    setShowDeleteConfirm(true);
  };

  // Elimina membro
  const handleDeleteMember = async () => {
    if (!selectedMember) return;
    
    setDeletingMember(true);
    
    try {
      await axios.delete(`${API}/associations/${user?.association_id}/members/${selectedMember.id}`);
      toast.success('Membro rimosso dall\'associazione');
      setShowDeleteConfirm(false);
      setSelectedMember(null);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setDeletingMember(false);
    }
  };

  // Genera password per edit
  const generateEditPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%&';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setEditMemberForm({ ...editMemberForm, new_password: password });
  };

  // Crea nuovo admin
  const handleCreateAdmin = async () => {
    if (!adminForm.name || !adminForm.email || !adminForm.password) {
      toast.error('Compila tutti i campi obbligatori');
      return;
    }
    
    if (adminForm.password.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri');
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/associations/${user?.association_id}/create-admin`, {
        name: adminForm.name,
        email: adminForm.email,
        password: adminForm.password,
        company_name: adminForm.company_name || `Admin ${association?.name}`
      });
      
      toast.success('Admin creato con successo!');
      setShowAddAdminDialog(false);
      resetAdminForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle stato membro
  const handleToggleMemberStatus = async (member: AssociationMember) => {
    try {
      await axios.put(`${API}/associations/${user?.association_id}/members/${member.id}`, {
        is_active: !member.is_active
      });
      toast.success(`Membro ${member.is_active ? 'disattivato' : 'attivato'}`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // Reset form
  const resetAdminForm = () => {
    setAdminForm({
      name: '',
      email: '',
      password: '',
      company_name: '',
      permissions: {
        can_manage_members: true,
        can_manage_sellers: true,
        can_manage_groupage: true,
        can_manage_settings: false,
        can_view_analytics: true,
        can_manage_admins: false
      }
    });
    setShowPassword(false);
  };

  const resetMemberForm = () => {
    setMemberForm({
      email: '',
      name: '',
      company_name: '',
      member_code: '',
      membership_type: 'standard',
      notes: ''
    });
  };

  // Aggiungi nuovo membro
  const handleAddMember = async () => {
    if (!memberForm.email || !memberForm.company_name) {
      toast.error('Email e ragione sociale sono obbligatori');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/associations/${user?.association_id}/members`, {
        email: memberForm.email,
        name: memberForm.name || memberForm.company_name,
        company_name: memberForm.company_name,
        member_code: memberForm.member_code,
        membership_type: memberForm.membership_type,
        notes: memberForm.notes
      });
      
      const tempPassword = response.data?.temp_password;
      if (tempPassword) {
        toast.success(
          <div>
            <p><strong>Membro creato!</strong></p>
            <p className="text-sm mt-1">Password temporanea:</p>
            <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">{tempPassword}</code>
            <p className="text-xs mt-1 text-muted-foreground">Comunica queste credenziali al membro</p>
          </div>,
          { duration: 15000 }
        );
      } else {
        toast.success('Membro aggiunto con successo!');
      }
      
      setShowAddMemberDialog(false);
      resetMemberForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Aggiungi nuovo admin
  const handleAddAdmin = async () => {
    if (!adminForm.email || !adminForm.name || !adminForm.password) {
      toast.error('Nome, email e password sono obbligatori');
      return;
    }
    
    if (adminForm.password.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/associations/${user?.association_id}/create-admin`, {
        name: adminForm.name,
        email: adminForm.email,
        password: adminForm.password,
        company_name: adminForm.company_name || `Admin ${adminForm.name}`,
        permissions: adminForm.permissions
      });
      toast.success('Amministratore creato con successo!');
      setShowAddAdminDialog(false);
      resetAdminForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSubmitting(false);
    }
  };

  // Filtra membri
  const filteredMembers = members.filter(member => {
    const matchesSearch = 
      member.company?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.member_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      member.company?.email?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = 
      statusFilter === 'all' || 
      (statusFilter === 'active' && member.is_active) ||
      (statusFilter === 'inactive' && !member.is_active);
    
    const matchesVerified = 
      verifiedFilter === 'all' ||
      (verifiedFilter === 'verified' && member.is_verified) ||
      (verifiedFilter === 'unverified' && !member.is_verified);
    
    return matchesSearch && matchesStatus && matchesVerified;
  });

  // Conteggi per filtri
  const counts = {
    all: members.length,
    active: members.filter(m => m.is_active).length,
    inactive: members.filter(m => !m.is_active).length,
    verified: members.filter(m => m.is_verified).length,
    unverified: members.filter(m => !m.is_verified).length
  };

  if (!user?.is_association_admin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
        <Shield className="h-16 w-16 text-muted-foreground mb-4" />
        <h2 className="text-xl font-semibold mb-2">Accesso Riservato</h2>
        <p className="text-muted-foreground">
          Questa pagina è riservata agli amministratori dell'associazione.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Caricamento...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="members-management-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Gestione Associati</h1>
          <p className="text-muted-foreground mt-1">
            {association?.name} • {members.length} membri • {admins.length} amministratori
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchData} size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Aggiorna
          </Button>
          <Button 
            onClick={() => activeTab === 'members' ? setShowAddMemberDialog(true) : setShowAddAdminDialog(true)}
            data-testid="add-member-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            {activeTab === 'members' ? 'Nuovo Membro' : 'Nuovo Admin'}
          </Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Users className="h-5 w-5" />}
          label="Membri Totali"
          value={members.length}
          color="primary"
        />
        <StatCard
          icon={<UserCheck className="h-5 w-5" />}
          label="Attivi"
          value={counts.active}
          subValue={`${Math.round(counts.active / members.length * 100 || 0)}%`}
          color="success"
        />
        <StatCard
          icon={<BadgeCheck className="h-5 w-5" />}
          label="Verificati"
          value={counts.verified}
          color="info"
        />
        <StatCard
          icon={<Crown className="h-5 w-5" />}
          label="Amministratori"
          value={admins.length}
          color="warning"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'members' | 'admins')}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <TabsList className="grid w-full sm:w-auto grid-cols-2">
            <TabsTrigger value="members" className="gap-2">
              <Users className="h-4 w-4" />
              Membri
              <Badge variant="secondary" className="ml-1">{members.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="admins" className="gap-2">
              <ShieldCheck className="h-4 w-4" />
              Admin
              <Badge variant="secondary" className="ml-1">{admins.length}</Badge>
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Members Tab */}
        <TabsContent value="members" className="space-y-4 mt-6">
          {/* Filtri */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col lg:flex-row gap-4">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per nome, codice o email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="member-search"
                  />
                </div>
                
                {/* Filter Pills */}
                <div className="flex items-center gap-2 flex-wrap">
                  <FilterPill
                    label="Tutti"
                    active={statusFilter === 'all'}
                    onClick={() => setStatusFilter('all')}
                    count={counts.all}
                  />
                  <FilterPill
                    label="Attivi"
                    active={statusFilter === 'active'}
                    onClick={() => setStatusFilter('active')}
                    count={counts.active}
                  />
                  <FilterPill
                    label="Inattivi"
                    active={statusFilter === 'inactive'}
                    onClick={() => setStatusFilter('inactive')}
                    count={counts.inactive}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Members List */}
          {filteredMembers.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Users className="h-16 w-16 text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-1">Nessun membro trovato</h3>
                <p className="text-muted-foreground text-center max-w-sm">
                  {searchQuery ? 'Prova a modificare i criteri di ricerca' : 'Inizia aggiungendo il primo membro all\'associazione'}
                </p>
                {!searchQuery && (
                  <Button className="mt-4" onClick={() => setShowAddMemberDialog(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Aggiungi Membro
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[300px]">Membro</TableHead>
                    <TableHead>Codice Socio</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead>Data Iscrizione</TableHead>
                    <TableHead className="text-right">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((member) => (
                    <TableRow key={member.id} className="group">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <MemberAvatar name={member.company?.name || 'N/A'} />
                          <div>
                            <div className="font-medium flex items-center gap-2">
                              {member.company?.name || 'N/A'}
                              {member.is_verified && (
                                <BadgeCheck className="h-4 w-4 text-blue-500" />
                              )}
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              {member.company?.email && (
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {member.company.email}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {member.member_code ? (
                          <code className="px-2 py-1 bg-muted rounded text-sm font-mono">
                            {member.member_code}
                          </code>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {member.membership_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={member.is_active ? 'default' : 'secondary'}
                          className={cn(
                            member.is_active 
                              ? 'bg-green-500/10 text-green-600 hover:bg-green-500/20' 
                              : 'bg-gray-500/10 text-gray-600'
                          )}
                        >
                          {member.is_active ? 'Attivo' : 'Inattivo'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {format(parseISO(member.created_at), 'd MMM yyyy', { locale: it })}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-primary"
                            onClick={() => openEditMemberDialog(member)}
                            data-testid={`edit-member-${member.id}`}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                            onClick={() => confirmDeleteMember(member)}
                            data-testid={`delete-member-${member.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* Admins Tab */}
        <TabsContent value="admins" className="space-y-4 mt-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {admins.map((admin) => (
              <Card key={admin.id} className={cn(
                "transition-all hover:shadow-md",
                admin.is_primary && "ring-2 ring-primary/20"
              )}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <MemberAvatar name={admin.user?.name || 'Admin'} size="lg" />
                      <div>
                        <CardTitle className="text-base flex items-center gap-2">
                          {admin.user?.name || 'Admin'}
                          {admin.is_primary && (
                            <Crown className="h-4 w-4 text-yellow-500" />
                          )}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          {admin.user?.email}
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pb-3">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Permessi
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {admin.can_manage_members && (
                        <Badge variant="secondary" className="text-xs">Membri</Badge>
                      )}
                      {admin.can_manage_sellers && (
                        <Badge variant="secondary" className="text-xs">Fornitori</Badge>
                      )}
                      {admin.can_manage_groupage && (
                        <Badge variant="secondary" className="text-xs">Groupage</Badge>
                      )}
                      {admin.can_manage_settings && (
                        <Badge variant="secondary" className="text-xs">Impostazioni</Badge>
                      )}
                      {admin.can_view_analytics && (
                        <Badge variant="secondary" className="text-xs">Analytics</Badge>
                      )}
                      {admin.can_manage_admins && (
                        <Badge variant="secondary" className="text-xs">Admin</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-3 border-t">
                  <div className="flex items-center justify-between w-full">
                    <span className="text-xs text-muted-foreground">
                      Dal {format(parseISO(admin.created_at), 'd MMM yyyy', { locale: it })}
                    </span>
                    {!admin.is_primary && (
                      <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </CardFooter>
              </Card>
            ))}

            {/* Add Admin Card */}
            <Card 
              className="border-dashed cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-all"
              onClick={() => setShowAddAdminDialog(true)}
            >
              <CardContent className="flex flex-col items-center justify-center h-full min-h-[200px]">
                <div className="p-4 rounded-full bg-muted mb-3">
                  <UserPlus className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="font-medium">Aggiungi Admin</p>
                <p className="text-sm text-muted-foreground">Crea un nuovo amministratore</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog: Aggiungi Admin */}
      <Dialog open={showAddAdminDialog} onOpenChange={(open) => {
        setShowAddAdminDialog(open);
        if (!open) resetAdminForm();
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <ShieldCheck className="h-5 w-5 text-primary" />
              </div>
              Nuovo Amministratore
            </DialogTitle>
            <DialogDescription>
              Crea un nuovo utente con privilegi di amministratore per {association?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4">
            {/* Dati Personali */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Dati Personali
              </h4>
              
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="admin-name">Nome Completo *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin-name"
                      value={adminForm.name}
                      onChange={(e) => setAdminForm({...adminForm, name: e.target.value})}
                      placeholder="es. Mario Rossi"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin-email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin-email"
                      type="email"
                      value={adminForm.email}
                      onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
                      placeholder="admin@esempio.it"
                      className="pl-10"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admin-company">Nome Azienda</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin-company"
                      value={adminForm.company_name}
                      onChange={(e) => setAdminForm({...adminForm, company_name: e.target.value})}
                      placeholder={`Admin ${association?.name || ''}`}
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Password */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Credenziali di Accesso
              </h4>
              
              <div className="space-y-2">
                <Label htmlFor="admin-password">Password *</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="admin-password"
                      type={showPassword ? 'text' : 'password'}
                      value={adminForm.password}
                      onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                      placeholder="Minimo 8 caratteri"
                      className="pl-10 pr-10 font-mono"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button type="button" variant="outline" onClick={generatePassword}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Genera
                  </Button>
                </div>
              </div>
            </div>

            {/* Riepilogo Credenziali */}
            {(adminForm.email || adminForm.password) && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium">Riepilogo Credenziali</h4>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(
                      `Email: ${adminForm.email}\nPassword: ${adminForm.password}`,
                      'Credenziali'
                    )}
                  >
                    <Copy className="h-4 w-4 mr-1" />
                    Copia
                  </Button>
                </div>
                <div className="space-y-2 font-mono text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-20">Email:</span>
                    <span className="font-medium">{adminForm.email || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground w-20">Password:</span>
                    <span className="font-medium">{adminForm.password || '-'}</span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Comunica queste credenziali al nuovo amministratore
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAdminDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleCreateAdmin} 
              disabled={!adminForm.name || !adminForm.email || !adminForm.password || submitting}
            >
              {submitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4 mr-2" />
              )}
              Crea Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Dettaglio Membro */}
      <Dialog open={showMemberDetailDialog} onOpenChange={setShowMemberDetailDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Dettaglio Membro</DialogTitle>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-4">
                <MemberAvatar name={selectedMember.company?.name || 'N/A'} size="lg" />
                <div>
                  <h3 className="font-semibold text-lg">{selectedMember.company?.name}</h3>
                  {selectedMember.member_code && (
                    <code className="text-sm bg-muted px-2 py-0.5 rounded">
                      {selectedMember.member_code}
                    </code>
                  )}
                </div>
              </div>
              
              <Separator />
              
              <div className="grid gap-3">
                {selectedMember.company?.email && (
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedMember.company.email}</span>
                  </div>
                )}
                {selectedMember.company?.phone && (
                  <div className="flex items-center gap-3">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedMember.company.phone}</span>
                  </div>
                )}
                {selectedMember.company?.city && (
                  <div className="flex items-center gap-3">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedMember.company.city} {selectedMember.company.province && `(${selectedMember.company.province})`}</span>
                  </div>
                )}
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Stato</span>
                <Badge variant={selectedMember.is_active ? 'default' : 'secondary'}>
                  {selectedMember.is_active ? 'Attivo' : 'Inattivo'}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Verificato</span>
                <Badge variant={selectedMember.is_verified ? 'default' : 'outline'}>
                  {selectedMember.is_verified ? 'Sì' : 'No'}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Tipo Membership</span>
                <Badge variant="outline" className="capitalize">
                  {selectedMember.membership_type}
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Data Iscrizione</span>
                <span>{format(parseISO(selectedMember.created_at), 'd MMMM yyyy', { locale: it })}</span>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMemberDetailDialog(false)}>
              Chiudi
            </Button>
            <Button onClick={() => {
              setShowMemberDetailDialog(false);
              if (selectedMember) openEditMemberDialog(selectedMember);
            }}>
              <Edit className="h-4 w-4 mr-2" />
              Modifica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Modifica Membro */}
      <Dialog open={showEditMemberDialog} onOpenChange={setShowEditMemberDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Edit className="h-5 w-5 text-primary" />
              </div>
              Modifica Membro
            </DialogTitle>
            <DialogDescription>
              Modifica i dati del membro {selectedMember?.company?.name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-5 py-4 max-h-[60vh] overflow-y-auto">
            {/* Dati Utente */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Dati Utente
              </h4>
              
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Nome / Ragione Sociale</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-name"
                      value={editMemberForm.name}
                      onChange={(e) => setEditMemberForm({...editMemberForm, name: e.target.value})}
                      placeholder="Nome azienda o persona"
                      className="pl-10"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-email"
                      type="email"
                      value={editMemberForm.email}
                      onChange={(e) => setEditMemberForm({...editMemberForm, email: e.target.value})}
                      placeholder="email@esempio.it"
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Email utilizzata per l'accesso alla piattaforma
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-phone">Telefono</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-phone"
                      value={editMemberForm.phone}
                      onChange={(e) => setEditMemberForm({...editMemberForm, phone: e.target.value})}
                      placeholder="+39 ..."
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
            </div>

            <Separator />

            {/* Info Membership */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Membership
              </h4>
              
              <div className="grid gap-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-member-code">Codice Socio</Label>
                    <Input
                      id="edit-member-code"
                      value={editMemberForm.member_code}
                      onChange={(e) => setEditMemberForm({...editMemberForm, member_code: e.target.value})}
                      placeholder="es. FIDA-0001"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-membership-type">Tipo Membership</Label>
                    <Select 
                      value={editMemberForm.membership_type}
                      onValueChange={(v) => setEditMemberForm({...editMemberForm, membership_type: v})}
                    >
                      <SelectTrigger id="edit-membership-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="honorary">Onorario</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit-notes">Note</Label>
                  <Textarea
                    id="edit-notes"
                    value={editMemberForm.notes}
                    onChange={(e) => setEditMemberForm({...editMemberForm, notes: e.target.value})}
                    placeholder="Note interne sul membro..."
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Stati */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Stato
              </h4>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Membro Attivo</Label>
                    <p className="text-sm text-muted-foreground">
                      I membri inattivi non possono accedere ai Groupage
                    </p>
                  </div>
                  <Switch
                    checked={editMemberForm.is_active}
                    onCheckedChange={(v) => setEditMemberForm({...editMemberForm, is_active: v})}
                  />
                </div>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Verificato</Label>
                    <p className="text-sm text-muted-foreground">
                      Indica che l'identità del membro è stata verificata
                    </p>
                  </div>
                  <Switch
                    checked={editMemberForm.is_verified}
                    onCheckedChange={(v) => setEditMemberForm({...editMemberForm, is_verified: v})}
                  />
                </div>
              </div>
            </div>

            <Separator />

            {/* Reset Password */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                Reset Password
              </h4>
              
              <div className="space-y-2">
                <Label htmlFor="edit-new-password">Nuova Password</Label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="edit-new-password"
                      type={showNewPassword ? 'text' : 'password'}
                      value={editMemberForm.new_password}
                      onChange={(e) => setEditMemberForm({...editMemberForm, new_password: e.target.value})}
                      placeholder="Lascia vuoto per non modificare"
                      className="pl-10 pr-10 font-mono"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                    >
                      {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <Button type="button" variant="outline" onClick={generateEditPassword}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Genera
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Minimo 8 caratteri. Lascia vuoto se non vuoi cambiare la password.
                </p>
              </div>
              
              {editMemberForm.new_password && editMemberForm.new_password.length >= 8 && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-yellow-600 text-sm font-medium mb-1">
                    <Key className="h-4 w-4" />
                    Nuova password impostata
                  </div>
                  <code className="text-sm bg-background px-2 py-1 rounded">
                    {editMemberForm.new_password}
                  </code>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="ml-2"
                    onClick={() => copyToClipboard(editMemberForm.new_password, 'Password')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditMemberDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleSaveMember} disabled={savingMember}>
              {savingMember ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Check className="h-4 w-4 mr-2" />
              )}
              Salva Modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alert Dialog: Conferma Eliminazione */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Conferma Rimozione
            </AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler rimuovere <strong>{selectedMember?.company?.name}</strong> dall'associazione?
              <br /><br />
              Questa azione non può essere annullata. Il membro perderà l'accesso ai Groupage e alle funzionalità dell'associazione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deletingMember}
            >
              {deletingMember ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Rimuovi Membro
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Nuovo Membro */}
      <Dialog open={showAddMemberDialog} onOpenChange={(open) => {
        setShowAddMemberDialog(open);
        if (!open) resetMemberForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Nuovo Membro
            </DialogTitle>
            <DialogDescription>
              Aggiungi un nuovo membro all'associazione
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="member-company">Ragione Sociale *</Label>
              <Input
                id="member-company"
                value={memberForm.company_name}
                onChange={(e) => setMemberForm({ ...memberForm, company_name: e.target.value })}
                placeholder="es. Ristorante Da Mario srl"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="member-name">Nome Referente</Label>
              <Input
                id="member-name"
                value={memberForm.name}
                onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })}
                placeholder="es. Mario Rossi"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="member-email">Email *</Label>
              <Input
                id="member-email"
                type="email"
                value={memberForm.email}
                onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })}
                placeholder="email@esempio.com"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="member-code">Codice Socio</Label>
                <Input
                  id="member-code"
                  value={memberForm.member_code}
                  onChange={(e) => setMemberForm({ ...memberForm, member_code: e.target.value })}
                  placeholder="es. FIDA-0042"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select
                  value={memberForm.membership_type}
                  onValueChange={(v) => setMemberForm({ ...memberForm, membership_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="honorary">Onorario</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="member-notes">Note</Label>
              <Textarea
                id="member-notes"
                value={memberForm.notes}
                onChange={(e) => setMemberForm({ ...memberForm, notes: e.target.value })}
                placeholder="Note interne sul membro..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddMemberDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleAddMember} disabled={submitting}>
              {submitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Aggiungi Membro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Nuovo Admin */}
      <Dialog open={showAddAdminDialog} onOpenChange={(open) => {
        setShowAddAdminDialog(open);
        if (!open) resetAdminForm();
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Nuovo Amministratore
            </DialogTitle>
            <DialogDescription>
              Crea un nuovo account amministratore per l'associazione
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="admin-name">Nome *</Label>
              <Input
                id="admin-name"
                value={adminForm.name}
                onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })}
                placeholder="es. Mario Rossi"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin-email">Email *</Label>
              <Input
                id="admin-email"
                type="email"
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                placeholder="admin@associazione.it"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin-password">Password *</Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="admin-password"
                    type={showPassword ? "text" : "password"}
                    value={adminForm.password}
                    onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                    placeholder="Minimo 8 caratteri"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-3"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={generatePassword}
                >
                  Genera
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Minimo 8 caratteri. Condividi queste credenziali con l'amministratore.
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="admin-company">Ragione Sociale</Label>
              <Input
                id="admin-company"
                value={adminForm.company_name}
                onChange={(e) => setAdminForm({ ...adminForm, company_name: e.target.value })}
                placeholder="es. Amministrazione FIDA"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddAdminDialog(false)}>
              Annulla
            </Button>
            <Button onClick={handleAddAdmin} disabled={submitting}>
              {submitting ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <ShieldCheck className="h-4 w-4 mr-2" />
              )}
              Crea Amministratore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MembersManagementPage;
